/**
 * T064 — collection page infinite scroll.
 *
 * Upgrades the server-rendered "Load more" link (snippets/pagination-load-more.liquid)
 * into fetch-and-append pagination: the next page is fetched through the Section
 * Rendering API and its grid items are appended to the existing `.js-grid`.
 *
 * Behaviour:
 * - Auto-loads pages as the control nears the viewport, up to `data-auto-pages`
 *   ('all' = every page, the behaviour that won the test). Once that budget is
 *   spent, or a fetch fails, the "Load more" button is shown instead.
 * - Appended product cards are custom elements (<product-grid-item>, <radio-swatch>)
 *   and the quick-add / card-slider features are document-delegated or
 *   MutationObserver-driven, so appended items are interactive without extra work.
 * - The address bar tracks the deepest loaded page via history.replaceState, with
 *   filter/sort params preserved, so reloads and shared links keep their place.
 * - When the theme's AJAX filters replace the grid contents, this element is
 *   destroyed with them and the freshly rendered one upgrades automatically,
 *   so infinite scroll keeps working after filtering or sorting.
 * - After a failed fetch the next click retries once; if that fails too, the
 *   link falls back to native navigation to the next page.
 */
class CollectionLoadMore extends HTMLElement {
  #observer = null;
  #controller = null;
  #loading = false;
  #failedAttempts = 0;
  #autoLoadsUsed = 0;
  #autoPages = 0;

  connectedCallback() {
    this.trigger = this.querySelector('[data-load-more-trigger]');
    this.status = this.querySelector('[data-load-more-status]');
    this.grid = this.closest('[data-products-grid]')?.querySelector('.js-grid');
    if (!this.trigger || !this.grid || !this.dataset.nextUrl) return;

    this.label = this.trigger.querySelector('.pagination-load-more__label');
    this.idleLabel = this.label?.textContent ?? '';
    this.#autoPages = this.dataset.autoPages === 'all' ? Infinity : Number(this.dataset.autoPages || 0);

    this.trigger.addEventListener('click', this.#onClick);

    if (this.#autoPages > 0 && 'IntersectionObserver' in window) {
      this.setAttribute('data-auto', '');
      this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '800px 0px' });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
    this.trigger?.removeEventListener('click', this.#onClick);
  }

  #onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    if (this.#autoLoadsUsed >= this.#autoPages) {
      this.#stopAutoLoading();
      return;
    }
    this.#autoLoadsUsed += 1;
    this.#load();
  };

  #onClick = (event) => {
    // Second consecutive failure: let the link navigate natively to the next page.
    if (this.#failedAttempts >= 2) return;
    event.preventDefault();
    this.#load();
  };

  async #load() {
    if (this.#loading || !this.dataset.nextUrl) return;
    this.#loading = true;
    this.removeAttribute('data-error');
    this.#setBusy(true);

    try {
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      this.#controller = new AbortController();
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = Array.from(doc.querySelectorAll('.js-grid > *'));
      this.grid.append(...items);

      this.#failedAttempts = 0;
      this.#updateAddressBar(url);
      this.#announce(this.dataset.announceLoaded.replace('[count]', String(items.length)));
      this.dispatchEvent(new CustomEvent('theme:collection:products-appended', { bubbles: true, detail: { items } }));

      const nextUrl = doc.querySelector('collection-load-more')?.dataset.nextUrl;
      if (!nextUrl || items.length === 0) {
        this.#complete();
        return;
      }

      this.dataset.nextUrl = nextUrl;
      this.trigger.href = nextUrl;
      this.#setBusy(false);

      if (this.#autoLoadsUsed >= this.#autoPages) {
        this.#stopAutoLoading();
      } else if (this.#observer) {
        // Re-arm so a short appended page that leaves us inside the root margin
        // still triggers the next load without waiting for a scroll event.
        this.#observer.unobserve(this);
        this.#observer.observe(this);
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.#fail();
    } finally {
      this.#loading = false;
    }
  }

  #complete() {
    delete this.dataset.nextUrl;
    this.#stopAutoLoading();
    const returnFocus = document.activeElement === this.trigger || this.trigger.contains(document.activeElement);
    this.removeAttribute('data-loading');
    this.setAttribute('data-complete', '');
    this.#announce(this.dataset.labelAllLoaded);
    if (returnFocus) {
      this.status.tabIndex = -1;
      this.status.focus();
    }
  }

  #fail() {
    this.#failedAttempts += 1;
    this.#stopAutoLoading();
    this.#setBusy(false);
    this.setAttribute('data-error', '');
    this.#announce(this.dataset.labelError);
  }

  /** Hand over to the visible "Load more" button. */
  #stopAutoLoading() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.removeAttribute('data-auto');
  }

  #setBusy(busy) {
    this.toggleAttribute('data-loading', busy);
    this.classList.toggle('is-loading', busy); // runs the theme's .svg-loader animation
    this.trigger.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (this.label) this.label.textContent = busy ? this.dataset.labelLoading : this.idleLabel;
    if (busy) this.#announce(this.dataset.labelLoading);
  }

  #announce(message) {
    if (this.status) this.status.textContent = message;
  }

  /** Keep filters/sort in the URL, drop the section_id fetch param, keep any router state. */
  #updateAddressBar(fetchUrl) {
    try {
      const url = new URL(fetchUrl);
      url.searchParams.delete('section_id');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
    } catch {
      // Address bar state is a nicety — never let it break loading.
    }
  }
}

if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', CollectionLoadMore);
}
